import { type FormEvent, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";

import { getErrorMessage } from "../../lib/errorMessage";
import { signIn, signInWithOAuth, signUp, type OAuthProvider } from "./api";

type Mode = "signin" | "signup";

type StrengthLevel = "weak" | "ok" | "strong";

const STRENGTH_META: Record<StrengthLevel, { labelKey: string; toneClass: string; barClass: string; filled: number }> = {
  weak: { labelKey: "auth.password_strength_weak", toneClass: "text-ember", barClass: "bg-ember", filled: 1 },
  ok: { labelKey: "auth.password_strength_ok", toneClass: "text-fg-mute", barClass: "bg-fg-mute", filled: 2 },
  strong: { labelKey: "auth.password_strength_strong", toneClass: "text-volt", barClass: "bg-volt", filled: 3 },
};

// 8 is Supabase's default `auth.password_min_length` — shorter is rejected
// server-side regardless of this scorer. The 10/12-char and class-count
// tiers above that are UX guidance only (drive the meter + submit gate);
// keep in lockstep with the server policy if it ever tightens.
function scorePassword(pw: string): StrengthLevel {
  if (pw.length < 8) return "weak";
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) => re.test(pw)).length;
  if (pw.length >= 12 && classes >= 3) return "strong";
  if (pw.length >= 10 || classes >= 3) return "ok";
  return "weak";
}

function AuthCard({ children }: { children: ReactNode }) {
  return <div className="rounded-xl border border-line bg-surface p-8 shadow-sm">{children}</div>;
}

export function LoginForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialError = params.get("error");
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(initialError);
  const [loading, setLoading] = useState(false);
  const [pendingConfirmEmail, setPendingConfirmEmail] = useState<string | null>(null);
  const submitHelpId = useId();

  const strength = mode === "signup" ? scorePassword(password) : null;
  const passwordStrongEnough = strength !== null && strength !== "weak";
  const submitAllowed = mode === "signin" || (passwordStrongEnough && termsAccepted);
  let submitHelpKey: string;
  if (!passwordStrongEnough && !termsAccepted) submitHelpKey = "auth.submit_help_signup";
  else if (!termsAccepted) submitHelpKey = "auth.submit_help_terms_only";
  else submitHelpKey = "auth.submit_help_password_only";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (mode === "signup") {
      await runAuth(async () => {
        const result = await signUp(email, password);
        if (result.status === "session_established") navigate("/", { replace: true });
        else setPendingConfirmEmail(result.email);
      });
      return;
    }
    const ok = await runAuth(() => signIn(email, password));
    if (ok) navigate("/", { replace: true });
  }

  async function handleOAuth(provider: OAuthProvider) {
    await runAuth(() => signInWithOAuth(provider));
  }

  async function runAuth(action: () => Promise<void>): Promise<boolean> {
    setError(null);
    setLoading(true);
    try {
      await action();
      return true;
    } catch (err) {
      console.error("[auth] runAuth failed", err);
      setError(getErrorMessage(err, t("auth.generic_error")));
      return false;
    } finally {
      setLoading(false);
    }
  }

  if (pendingConfirmEmail) {
    return (
      <AuthCard>
        <h1 className="mb-3 text-2xl font-bold text-fg">{t("auth.signup_success_title")}</h1>
        <p className="mb-6 text-sm text-fg-mute">
          {t("auth.signup_success_body", { email: pendingConfirmEmail })}
        </p>
        <button
          type="button"
          onClick={() => {
            setPendingConfirmEmail(null);
            setEmail("");
            setPassword("");
          }}
          className="text-sm font-medium text-volt hover:underline"
        >
          {t("auth.signup_use_different_email")}
        </button>
      </AuthCard>
    );
  }

  const submitLabel = loading
    ? t("common.loading")
    : mode === "signin"
      ? t("auth.submit_signin")
      : t("auth.submit_signup");

  return (
    <AuthCard>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-fg">
          {mode === "signin" ? t("auth.signin_title") : t("auth.signup_title")}
        </h1>
        {mode === "signup" && (
          <p className="mt-2 text-sm text-fg-mute">{t("auth.signup_subtitle")}</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-fg-mute">
            {t("auth.email_label")}
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-volt focus:outline-none focus:ring-1 focus:ring-volt/30"
          />
        </div>

        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium text-fg-mute">
            {t("auth.password_label")}
          </label>
          <input
            id="password"
            type="password"
            autoComplete={mode === "signin" ? "current-password" : "new-password"}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-volt focus:outline-none focus:ring-1 focus:ring-volt/30"
          />
          {mode === "signup" && strength && (
            <PasswordStrengthMeter strength={strength} empty={password.length === 0} />
          )}
        </div>

        {mode === "signup" && (
          <div className="flex items-start gap-2 text-sm text-fg-mute">
            <input
              id="terms"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-0.5 size-4 rounded border-line text-volt focus:ring-volt/30"
            />
            <label htmlFor="terms">
              {t("auth.terms_accept_prefix")}{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-volt hover:underline"
              >
                {t("auth.terms_accept_link")}
              </a>
              {t("auth.terms_accept_suffix")}
            </label>
          </div>
        )}

        {error && (
          <p role="alert" className="text-sm text-ember">
            {error}
          </p>
        )}

        {mode === "signup" && !submitAllowed && !loading && (
          <p id={submitHelpId} className="text-xs text-fg-faint">
            {t(submitHelpKey)}
          </p>
        )}

        <button
          type="submit"
          disabled={loading || !submitAllowed}
          aria-describedby={mode === "signup" && !submitAllowed ? submitHelpId : undefined}
          className="w-full rounded-lg bg-volt px-4 py-2 text-sm font-medium text-bg-deep hover:bg-volt-deep disabled:bg-surface-raised disabled:text-fg-faint"
        >
          {submitLabel}
        </button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <hr className="flex-1 border-line" />
        <span className="text-xs text-fg-faint">{t("auth.or_continue_with")}</span>
        <hr className="flex-1 border-line" />
      </div>

      <div className="space-y-3">
        <button
          type="button"
          onClick={() => handleOAuth("google")}
          disabled={loading}
          className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-line px-4 py-2 text-sm font-medium text-fg-mute hover:bg-surface-sunk disabled:opacity-60"
        >
          <GoogleLogo />
          {t("auth.continue_with_google")}
        </button>
        <button
          type="button"
          onClick={() => handleOAuth("apple")}
          disabled={loading}
          className="flex w-full items-center justify-center gap-2.5 rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-black/90 disabled:opacity-60"
        >
          <AppleLogo />
          {t("auth.continue_with_apple")}
        </button>
      </div>

      <p className="mt-6 text-center text-sm text-fg-mute">
        {mode === "signin" ? t("auth.no_account") : t("auth.have_account")}{" "}
        <button
          type="button"
          onClick={() => {
            setMode(mode === "signin" ? "signup" : "signin");
            setError(null);
            setTermsAccepted(false);
          }}
          className="font-medium text-volt hover:underline"
        >
          {mode === "signin" ? t("auth.submit_signup") : t("auth.submit_signin")}
        </button>
      </p>
    </AuthCard>
  );
}

// Brand glyphs are decorative — the text label is the accessible name.
function GoogleLogo() {
  return (
    <svg viewBox="0 0 18 18" className="size-4" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.35 0-4.34-1.59-5.05-3.71H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.95 10.71A5.41 5.41 0 0 1 3.66 9c0-.59.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l2.99-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l2.99 2.33C4.66 5.17 6.65 3.58 9 3.58Z"
      />
    </svg>
  );
}

function AppleLogo() {
  return (
    <svg viewBox="0 0 384 512" className="size-4" fill="currentColor" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

function PasswordStrengthMeter({ strength, empty }: { strength: StrengthLevel; empty: boolean }) {
  const { t } = useTranslation();
  const meta = STRENGTH_META[strength];
  return (
    <div className="mt-2 space-y-1">
      <div className="flex gap-1" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full ${
              i < meta.filled && !empty ? meta.barClass : "bg-surface-raised"
            }`}
          />
        ))}
      </div>
      {!empty && (
        <p className={`text-xs ${meta.toneClass}`}>
          <span className="sr-only">{t("auth.password_strength_label")}: </span>
          {t(meta.labelKey)}
        </p>
      )}
      {empty && <p className="text-xs text-fg-faint">{t("auth.password_hint")}</p>}
    </div>
  );
}
